import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();

  // 'checking' | 'valid' | 'invalid' | 'done'
  const [state, setState] = useState("checking");
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-token/${token}`)
      .then((r) => { setEmail(r.data?.email || ""); setState("valid"); })
      .catch((err) => {
        setErrorMsg(
          err.response?.data?.detail ||
          (err.response
            ? "This reset link is invalid or has expired."
            : "We couldn't reach the server to check this link. Please check your connection and open the link again.")
        );
        setState("invalid");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw1 !== pw2) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post(`/auth/reset-password`, { token, new_password: pw1 });
      setState("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-border rounded-lg p-8 shadow-sm">
        <div className="mb-6">
          <img src="/logo.webp" alt="Sun Nutrafoods" className="h-14 w-auto object-contain -ml-2" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">
            Customer Relationship Management
          </div>
        </div>

        {state === "checking" && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="reset-checking">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your reset link…
          </div>
        )}

        {state === "invalid" && (
          <div className="space-y-4" data-testid="reset-invalid">
            <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-md">
              <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-orange-900">We couldn't open this reset link</div>
                <div className="text-sm text-orange-800 mt-1">{errorMsg}</div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Reset links are valid for 1 hour and can only be used once. Please request a new one from the login page.
            </p>
            <Link to="/login" className="inline-block">
              <Button className="bg-primary hover:bg-primary/90 text-white" data-testid="reset-back-to-login">
                Back to sign in
              </Button>
            </Link>
          </div>
        )}

        {state === "valid" && (
          <>
            <h2 className="font-heading text-2xl font-bold">Set a new password</h2>
            <p className="text-sm text-muted-foreground mt-1">
              For <span className="font-semibold text-neutral-800" data-testid="reset-email">{email}</span>
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pw1" className="text-xs uppercase tracking-wider">New password</Label>
                <Input id="pw1" type="password" data-testid="reset-password-1" minLength={6}
                  value={pw1} onChange={(e) => setPw1(e.target.value)} required
                  autoFocus className="mt-1.5" placeholder="Minimum 6 characters" />
              </div>
              <div>
                <Label htmlFor="pw2" className="text-xs uppercase tracking-wider">Confirm password</Label>
                <Input id="pw2" type="password" data-testid="reset-password-2" minLength={6}
                  value={pw2} onChange={(e) => setPw2(e.target.value)} required
                  className="mt-1.5" placeholder="Retype the same password" />
              </div>
              <Button type="submit" data-testid="reset-submit" disabled={saving}
                className="w-full bg-primary hover:bg-primary/90 text-white h-11">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save new password"}
              </Button>
            </form>
          </>
        )}

        {state === "done" && (
          <div className="space-y-4" data-testid="reset-done">
            <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-md">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-900">Password updated</div>
                <div className="text-sm text-emerald-800 mt-1">
                  You can now sign in with your new password.
                </div>
              </div>
            </div>
            <Button onClick={() => nav("/login")} data-testid="reset-go-login"
              className="bg-primary hover:bg-primary/90 text-white">
              Go to sign in
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
